import React from 'react';
import _compact from 'lodash/compact';
import _findIndex from 'lodash/findIndex';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';

import useToggle from '@lyearn/core/hooks/useToggle';
import { setIn } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { BellOutline } from '@lyearn/icons';
import { LinkButton, Stack } from '@lyearn/ui';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import { SwitchWithInfoProps } from '@/components/SwitchWithInfo/types';
import {
  NotificationActionTypeEnum,
  NotificationConfigurationType,
  NotificationTemplate,
  ReminderConfigurationType,
} from '@/types/schema';

import NotificationReminderDialog from '../../../../../notifications/components/NotificationReminderDialog';
import { useGetDefaultAssignmentReminderQuery } from '../../../../graphql/queries/generated/defaultAssignmentReminderConfig';
import { DEFAULT_REMINDER_CONFIG } from './conts';

interface SettingsProps {
  className?: string;
  notificationConfigurations?: NotificationConfigurationType[];
  switchPlacement?: SwitchWithInfoProps['switchPlacement'];
  onChange: (value: any) => void;
}

function SetReminder({
  reminderConfigurations,
  onSubmit: onSubmitProp,
}: {
  reminderConfigurations?: ReminderConfigurationType[];
  onSubmit: (reminderConfigurations: ReminderConfigurationType[]) => void;
}) {
  const { t } = useTranslation();
  const [isOpen, { on, off }] = useToggle();

  const onSubmit = React.useCallback(
    (reminderConfigurations: ReminderConfigurationType[]) => {
      onSubmitProp(reminderConfigurations);
      off();
    },
    [off, onSubmitProp],
  );

  return (
    <>
      <LinkButton
        className="group flex h-40 flex-row items-center justify-start rounded-4 py-2 px-4 px-2 hover:bg-bg-primary"
        color={'internal'}
        startIcon={<BellOutline className="h-20 w-20" />}
        onClick={on}>
        {t('AssignmentCreator:ManageReminders')}
      </LinkButton>
      {!isOpen ? null : (
        <NotificationReminderDialog
          initialReminderConfig={reminderConfigurations}
          onClose={off}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
}

function useNotificationDescription(groupedReminderConfig?: {
  [key in NotificationActionTypeEnum]: NotificationTemplate | undefined;
}) {
  const { t } = useTranslation();

  if (_isEmpty(groupedReminderConfig) || groupedReminderConfig == undefined) {
    return {
      canShow: false,
      description: '',
    };
  }

  let assignContentTasks =
    groupedReminderConfig[NotificationActionTypeEnum.AssignContent]?.tasksRequired;
  const isAssignContentEnabled = assignContentTasks?.bell || assignContentTasks?.email;

  let dueDateTasks =
    groupedReminderConfig[NotificationActionTypeEnum.DueDateReminder]?.tasksRequired;
  const isDueDateEnabled = dueDateTasks?.bell || dueDateTasks?.email;

  let contentOverdueTasks =
    groupedReminderConfig[NotificationActionTypeEnum.ContentOverdue]?.tasksRequired;
  const isContentOverdueEnabled = contentOverdueTasks?.bell || contentOverdueTasks?.email;

  const descriptions: string[] = _compact([
    isAssignContentEnabled ? t('AssignmentCreator:NotifyLearnersDescriptionContentAssigned') : null,
    isDueDateEnabled ? t('AssignmentCreator:NotifyLearnersDescriptionDueCompletion') : null,
    isContentOverdueEnabled ? t('AssignmentCreator:NotifyLearnersDescriptionOverdue') : null,
  ]);

  let description = `${t('AssignmentCreator:NotifyLearnersDescription')} ${t('Admin:Content')} ${
    descriptions[0]
  }`;

  if (descriptions.length === 2) {
    description += ` ${t('And')} ${descriptions[1]}`;
  } else if (descriptions.length === 3) {
    description += `, ${descriptions[1]} ${t('And')} ${descriptions[2]}`;
  }

  return {
    canShow: !!descriptions.length,
    description: description,
    isDueDateEnabled,
  };
}

function NotificationSettings(props: SettingsProps) {
  const { className, notificationConfigurations, switchPlacement, onChange } = props;
  const { t } = useTranslation();

  const [{ fetching, data }] = useGetDefaultAssignmentReminderQuery();

  const groupedReminderConfig = React.useMemo(() => {
    return !data?.notificationTemplates?.edges
      ? undefined
      : _reduce(
          data?.notificationTemplates?.edges,
          (result, edge) => {
            return {
              ...result,
              [edge.node.actionType]: edge.node,
            };
          },
          {} as { [key in NotificationActionTypeEnum]: NotificationTemplate | undefined },
        );
  }, [data?.notificationTemplates?.edges]);

  const { canShow, description, isDueDateEnabled } =
    useNotificationDescription(groupedReminderConfig);

  const due_date_notification_config_index = React.useMemo(
    () =>
      _findIndex(
        notificationConfigurations,
        (config) => config.actionType === NotificationActionTypeEnum.DueDateReminder,
      ),
    [notificationConfigurations],
  );

  const onToggle = React.useCallback(
    (newValue: any) => {
      const newNotificationConfigurations = _map(notificationConfigurations, (config) => ({
        ...config,
        notifyLearners: newValue,
      }));

      onChange(newNotificationConfigurations);
    },
    [notificationConfigurations, onChange],
  );

  const onSetReminder = React.useCallback(
    (reminderConfigs: ReminderConfigurationType[]) => {
      onChange(
        due_date_notification_config_index === -1
          ? DEFAULT_REMINDER_CONFIG(reminderConfigs)
          : setIn(
              notificationConfigurations,
              [due_date_notification_config_index, 'reminderConfigurations'],
              reminderConfigs,
            ),
      );
    },
    [due_date_notification_config_index, notificationConfigurations, onChange],
  );

  let due_date_notification_config =
    groupedReminderConfig?.[NotificationActionTypeEnum.DueDateReminder];
  React.useEffect(() => {
    if (
      _isEmpty(notificationConfigurations) &&
      due_date_notification_config?.reminderConfigurations
    ) {
      onSetReminder(due_date_notification_config.reminderConfigurations);
    }
  }, [due_date_notification_config, notificationConfigurations, onSetReminder]);

  const canNotifyLearners = Boolean(
    notificationConfigurations?.[due_date_notification_config_index]?.notifyLearners,
  );

  if (!canShow) {
    return null;
  }

  return (
    <>
      <Stack className={className} direction="col">
        <SwitchWithInfo
          checked={canNotifyLearners}
          description={fetching ? '-' : description}
          label={t('AssignmentCreator:NotifyLearnersTitle')}
          switchPlacement={switchPlacement}
          onChange={onToggle}
        />
        {!canNotifyLearners || !isDueDateEnabled ? null : (
          <SetReminder
            reminderConfigurations={
              notificationConfigurations?.[due_date_notification_config_index]
                ?.reminderConfigurations
            }
            onSubmit={onSetReminder}
          />
        )}
      </Stack>
    </>
  );
}

export default React.memo(NotificationSettings);
